import { getParamUrl } from 'helpers/getParamsUrl';
import { IProducerProductsResponse, IProductNew } from 'interfaces/product';
import { BehaviorSubject, Observable } from 'rxjs';
import rxjsOperators from 'rxjs-operators';

import apiService, { ApiService } from './api';
import hotjarService from './hotjarService';
import tokenService from './token';

export class ProductNewService {
  public selectedProduct$ = new BehaviorSubject<IProductNew>(null);
  private products$ = new BehaviorSubject<IProductNew[]>(null);
  private searchText = new BehaviorSubject<string>('');
  private productWithoutSelected$ = new BehaviorSubject<IProductNew[]>(null);

  constructor(private api: ApiService) {}

  public loadProducts() {
    return tokenService.getTokens().pipe(
      rxjsOperators.take(1),
      rxjsOperators.switchMap(() => this.api.get<IProducerProductsResponse>('/product/producer')),
      rxjsOperators.tap(response => {
        this.products$.next(response.products);
        hotjarService.setShowSurvey(response.showSurvey);
      })
    );
  }

  public openProductDrawerByParam() {
    const productId = getParamUrl('productId');
    const url = !productId ? '/' : `?productId=${productId}`;

    window.history.pushState(null, null, url);
  }

  public getProducts(): Observable<IProductNew[]> {
    return this.products$.asObservable();
  }

  public setProductsWithoutSelected(selectedProduct?: IProductNew) {
    this.productWithoutSelected$.next(this.removeSelectedProductOfProducts(selectedProduct));
  }

  public getProductsWithoutSelected(): Observable<IProductNew[]> {
    return this.productWithoutSelected$.asObservable();
  }

  public setSelectedProduct(product: IProductNew) {
    this.selectedProduct$.next(product);
  }

  public getSelectedProduct() {
    return this.selectedProduct$.asObservable();
  }

  public refreshProducts() {
    this.products$.next(null);
    this.loadProducts().subscribe(() => {});
  }

  public unselectProduct() {
    this.selectedProduct$.next(null);
  }

  public setFilterProducts(text: string) {
    this.searchText.next(text);
  }

  public getFilterProducts(): Observable<string> {
    return this.searchText.asObservable();
  }

  public filterProductsBySearch(search: string) {
    const products = this.productWithoutSelected$.value;
    if (!search.length) {
      return products;
    }

    return products?.filter(product => {
      const hasMatch = this.searchProduct(search, product);
      return hasMatch || product?.children?.find(child => this.searchProduct(search, child));
    });
  }

  private searchProduct(search: string, product: IProductNew) {
    const inputValue = search.toLowerCase().trim();
    const fullProductName = `${product?.id} - ${product?.name}`.toLowerCase().trim();
    return fullProductName.includes(inputValue);
  }

  private removeSelectedProductOfProducts(selectedProduct?: IProductNew) {
    const products = this.products$.value;

    if (!selectedProduct) {
      return [];
    }

    return products?.reduce((acc: IProductNew[], { children, ...product }) => {
      if (product?.id === selectedProduct?.id) {
        return acc;
      }

      if (!children?.length) {
        acc.push.apply(acc, [product]);
        return acc;
      }

      const childrenWithoutSelected = children?.filter(({ id }) => id !== selectedProduct?.id);

      if (childrenWithoutSelected?.length) {
        acc.push.apply(acc, [{ ...product, children: childrenWithoutSelected }]);
        return acc;
      }

      return acc;
    }, []);
  }
}

export const ProductNewServiceFactory = () => new ProductNewService(apiService);

export const productNewService = ProductNewServiceFactory();
